import React, { useCallback } from 'react';
import { FiArrowUp } from 'react-icons/fi';
import styles from './SectionRail.module.css';

/**
 * Desktop Section Rail - Sticky right-side TOC navigation
 * Shows active section highlight and smooth-scrolls to sections
 */
const SectionRail = ({ sections = [], activeSectionId = null, onSectionSelect }) => {
  const handleScrollToTop = useCallback(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
  }, []);

  const handleSectionClick = useCallback((sectionId) => {
    if (onSectionSelect) {
      // Use parent's scroll handler (preferred - includes programmatic scroll flag)
      onSectionSelect(sectionId);
    } else {
      // Fallback: direct scroll with proper offset
      const anchorId = `section-${sectionId}`;
      const anchorElement = document.getElementById(anchorId);
      const sectionElement = document.getElementById(sectionId);
      const element = anchorElement || sectionElement;
      
      if (element) {
        const offset = 120; // Account for sticky header
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - offset;
        
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        window.scrollTo({
          top: offsetPosition,
          behavior: prefersReducedMotion ? 'auto' : 'smooth'
        });
      }
    }
  }, [onSectionSelect]);

  return (
    <aside className={styles.rail} aria-label="On this page">
      <div className={styles.railContent}>
        <h3 className={styles.railTitle}>On this page</h3>
        
        {/* Back to top */}
        <button
          className={styles.backToTop}
          onClick={handleScrollToTop}
          aria-label="Back to top"
        >
          <FiArrowUp size={16} />
          <span>Back to top</span>
        </button>

        {/* Sections list */}
        <nav className={styles.railNav} aria-label="Page sections">
          <ul className={styles.railList}>
            {sections.map((section) => {
              const isActive = activeSectionId === section.id;
              return (
                <li key={section.id} className={styles.railItem}>
                  <button
                    className={`${styles.railLink} ${isActive ? styles.railLinkActive : ''}`}
                    onClick={() => handleSectionClick(section.id)}
                    aria-current={isActive ? 'true' : undefined}
                    aria-label={`Jump to ${section.label}`}
                  >
                    <span className={styles.railLinkIcon}>
                      {section.icon}
                    </span>
                    <span className={styles.railLinkLabel}>{section.label}</span>
                    {isActive && <span className={styles.railLinkIndicator} aria-hidden="true" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default SectionRail;

